/**
 * Open positions table with per-row Close button + stop-loss column.
 *
 * For each open position we surface:
 *   • Price level of the attached stop-loss / take-profit (or "—" if none)
 *   • Live "distance to stop" percentage: how much of the entry→stop price
 *     distance is still remaining before the stop-loss trigger fires.
 *     Green when comfortable (>50% still remaining), amber when tightening
 *     (25-50%), red when about to trip (<25%).
 *
 * Sept 2026: stop-loss/take-profit trigger purely on the instrument's live
 * PRICE crossing a stored price level (`stop_loss_price`/`take_profit_price`)
 * — not on floating PnL — so this widget mirrors that basis. Actual
 * auto-close is still driven server-side in the WS PnL loop
 * (`_check_stop_losses`).
 */
import { Fragment, useState } from "react";
import { Button } from "@/components/ui/button";
import { ShieldAlert, Target, GitBranch, X as XIcon, LineChart as LineChartIcon } from "lucide-react";
import InfoTooltip from "@/components/InfoTooltip";
import InstrumentChartWidget from "@/components/chart/InstrumentChartWidget";
import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";
import { signTone, toneClassName, signedPrefix } from "@/lib/numberColor";

/**
 * distanceToStop — returns { pct, tone, sl } describing how close a
 * position's LIVE PRICE is to crossing its stop-loss price level.
 */
function distanceToStop(p, live) {
    const slPrice = Number(p.stop_loss_price || 0);
    if (!slPrice) return null;
    const entry = Number(p.entry_price || 0);
    const cur   = Number((live && (live[p.symbol] || live[p.symbol?.toUpperCase()])) || 0);
    if (!entry || !cur) return { pct: null, tone: "muted", sl: slPrice };
    // % of the entry→stop price distance still remaining before the trigger.
    const totalDistance = Math.abs(entry - slPrice);
    const remaining = p.side === "buy" ? (cur - slPrice) : (slPrice - cur);
    const pct = totalDistance > 0 ? Math.max(0, Math.min(100, (remaining / totalDistance) * 100)) : null;
    const tone = pct === null ? "muted" : pct > 50 ? "ok" : pct > 25 ? "warn" : "danger";
    return { pct: pct === null ? null : Math.round(pct), tone, sl: slPrice };
}

/**
 * floatingPnl — real-time unrealised PnL in KES for a position given the
 * current live price map. Returns null when we can't compute (missing
 * price feed) so the UI can render a muted em-dash instead of 0.
 *
 * Same math as distanceToStop().floatingKes but exposed as its own tiny
 * function so the new "PnL" column doesn't fabricate a value when the
 * position has no stop-loss attached (which is the common case).
 */
function floatingPnl(p, live) {
    const entry = Number(p.entry_price || 0);
    const qty   = Number(p.qty || 0);
    const cur   = Number((live && (live[p.symbol] || live[p.symbol?.toUpperCase()])) || 0);
    if (!entry || !qty || !cur) return null;
    const sign = p.side === "buy" ? 1 : -1;
    return Math.round((cur - entry) * qty * sign);
}

export function PositionsTable({ positions, onClose, busy, livePrices = {}, ocoByPosition = {}, onOpenAdvancedExit, onCancelOco }) {
    const [chartOpenId, setChartOpenId] = useState(null);
    const { sorted: sortedPositions, sortKey, direction, requestSort } = useSortableData(positions, {
        accessors: {
            symbol: (p) => p.symbol || "",
            side:   (p) => p.side || "",
            qty:    (p) => Number(p.qty || 0),
            entry:  (p) => Number(p.entry_price || 0),
            pnl:    (p) => { const v = floatingPnl(p, livePrices); return v === null ? -Infinity : v; },
            stop_loss:   (p) => Number(p.stop_loss_price || 0),
            take_profit: (p) => Number(p.take_profit_price || 0),
            copied: (p) => (p.is_copied ? 1 : 0),
        },
    });
    return (
        <div className="mt-6 border border-border bg-card rounded" data-testid="positions-table">
            <div className="p-3 border-b border-border flex items-center justify-between">
                <div className="overline">Open positions</div>
                <div className="text-xs text-muted-foreground">{positions.length} active</div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-xs text-muted-foreground">
                        <tr className="border-b border-border">
                            <SortableTh label="Symbol" sortKey="symbol" currentKey={sortKey} direction={direction} onSort={requestSort} />
                            <SortableTh label="Side"   sortKey="side"   currentKey={sortKey} direction={direction} onSort={requestSort} />
                            <SortableTh label="Qty"    sortKey="qty"    currentKey={sortKey} direction={direction} onSort={requestSort} align="right" />
                            <SortableTh label="Entry"  sortKey="entry"  currentKey={sortKey} direction={direction} onSort={requestSort} align="right" />
                            <th className="text-right px-3 py-2">
                                <span className="inline-flex items-center gap-1 justify-end w-full">
                                    <button type="button" onClick={() => requestSort("pnl")} data-testid="sort-th-pnl"
                                        className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${sortKey === "pnl" ? "text-foreground font-semibold" : ""}`}>
                                        PnL
                                    </button>
                                    <InfoTooltip term="Floating PnL">
                                        Live unrealised profit or loss in KES: (current price − entry) × qty, sign-flipped for shorts. Refreshes on every tick. Green = in profit, red = in loss. The "Close" button three columns to the right realises this value instantly.
                                    </InfoTooltip>
                                </span>
                            </th>
                            <th className="text-right px-3 py-2">
                                <span className="inline-flex items-center gap-1 justify-end w-full">
                                    <button type="button" onClick={() => requestSort("stop_loss")} data-testid="sort-th-stop_loss"
                                        className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${sortKey === "stop_loss" ? "text-foreground font-semibold" : ""}`}>
                                        <ShieldAlert className="w-3 h-3" /> Stop-loss
                                    </button>
                                    <InfoTooltip term="Stop Loss">
                                        The price level attached to this position at order time. When {" "}
                                        {"the instrument's live price"} crosses that level, the position auto-closes. "% to stop" tells you how much of the entry-to-stop distance is still remaining before the trigger fires.
                                    </InfoTooltip>
                                </span>
                            </th>
                            <th className="text-right px-3 py-2">
                                <span className="inline-flex items-center gap-1 justify-end w-full">
                                    <button type="button" onClick={() => requestSort("take_profit")} data-testid="sort-th-take_profit"
                                        className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${sortKey === "take_profit" ? "text-foreground font-semibold" : ""}`}>
                                        <Target className="w-3 h-3" /> Take-profit
                                    </button>
                                    <InfoTooltip term="Take Profit">
                                        The price level attached to this position at order time. When the instrument's live price reaches that level, the position auto-closes and locks in the win.
                                    </InfoTooltip>
                                </span>
                            </th>
                            <SortableTh label="Copied" sortKey="copied" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" />
                            <th className="text-right px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {positions.length === 0 && (
                            <tr><td colSpan="9" className="text-center py-8 text-muted-foreground">No open positions.</td></tr>
                        )}
                        {sortedPositions.map((p) => {
                            const d = distanceToStop(p, livePrices);
                            const pnl = floatingPnl(p, livePrices);
                            const pnlTone = pnl === null ? null : signTone(pnl, 0);
                            return (
                                <Fragment key={p.id}>
                                <tr className="border-b border-border/50">
                                    <td className="px-3 py-2 mono font-bold">
                                        {p.symbol}
                                        {p.origin_currency && p.origin_currency !== "KES" && (
                                            <span
                                                className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground align-middle"
                                                data-testid={`position-ccy-${p.id}`}
                                                title={`Quoted in ${p.origin_currency} · cleared in KES`}
                                            >
                                                {p.origin_currency}
                                            </span>
                                        )}
                                    </td>
                                    <td className={`px-3 py-2 uppercase text-xs font-bold ${p.side === "buy" ? "text-success" : "text-danger"}`}>{p.side}</td>
                                    <td className="px-3 py-2 mono text-right">{p.qty}</td>
                                    <td className="px-3 py-2 mono text-right">{p.entry_price?.toFixed(2)}</td>
                                    <td className="px-3 py-2 mono text-right" data-testid={`position-pnl-${p.id}`}>
                                        {pnl === null ? (
                                            <span className="text-muted-foreground">—</span>
                                        ) : (
                                            <span className={`font-bold proportional-nums ${toneClassName(pnlTone)}`}>
                                                {signedPrefix(pnl, 0)}{pnl.toLocaleString()}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right" data-testid={`position-sl-${p.id}`}>
                                        {d ? (
                                            <div className="inline-flex flex-col items-end leading-tight">
                                                <span className="mono text-xs">@ {d.sl.toFixed(2)}</span>
                                                {d.pct !== null && (
                                                    <span
                                                        className={`text-[10px] uppercase tracking-wider font-bold ${
                                                            d.tone === "ok"     ? "text-emerald-600 dark:text-emerald-400" :
                                                            d.tone === "warn"   ? "text-amber-600 dark:text-amber-400" :
                                                                                  "text-red-600 dark:text-red-400"
                                                        }`}
                                                    >
                                                        {d.pct}% to stop
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-muted-foreground">—</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right" data-testid={`position-tp-${p.id}`}>
                                        {Number(p.take_profit_price || 0) > 0 ? (
                                            <span className="mono text-xs text-emerald-600 dark:text-emerald-400">
                                                @ {Number(p.take_profit_price).toFixed(2)}
                                            </span>
                                        ) : (
                                            <span className="text-muted-foreground">—</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right text-xs">{p.is_copied ? <span className="text-accent">COPY</span> : "—"}</td>
                                    <td className="px-3 py-2 text-right">
                                        <div className="inline-flex gap-1.5">
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="text-xs"
                                                onClick={() => setChartOpenId(chartOpenId === p.id ? null : p.id)}
                                                data-testid={`view-chart-${p.id}`}
                                            >
                                                <LineChartIcon className="w-3 h-3 mr-1" /> Chart
                                            </Button>
                                            {p.side === "buy" && onOpenAdvancedExit && !ocoByPosition[p.id] && (
                                                <Button size="sm" variant="ghost" className="text-xs" onClick={() => onOpenAdvancedExit(p)} data-testid={`advanced-exit-${p.id}`}>
                                                    <GitBranch className="w-3 h-3 mr-1" /> Advanced Exit
                                                </Button>
                                            )}
                                            <Button size="sm" variant="outline" onClick={() => onClose(p)} disabled={busy} data-testid={`close-${p.id}`}>Close</Button>
                                        </div>
                                    </td>
                                </tr>
                                {chartOpenId === p.id && (
                                    <tr key={`${p.id}-chart`} className="border-b border-border/50 bg-muted/10" data-testid={`chart-row-${p.id}`}>
                                        <td colSpan="8" className="px-3 py-3">
                                            <InstrumentChartWidget symbol={p.symbol} compact />
                                        </td>
                                    </tr>
                                )}
                                {ocoByPosition[p.id] && (
                                    <tr key={`${p.id}-oco`} className="border-b border-border/50 bg-muted/20" data-testid={`oco-row-${p.id}`}>
                                        <td colSpan="8" className="px-3 py-2">
                                            <div className="flex items-center justify-between gap-3 pl-6 text-xs">
                                                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                                                    <GitBranch className="w-3 h-3" /> Linked OCO Strategy ({p.symbol})
                                                    {ocoByPosition[p.id].take_profit_price && (
                                                        <span className="text-emerald-600 dark:text-emerald-400 mono ml-2">TP {ocoByPosition[p.id].take_profit_price}</span>
                                                    )}
                                                    {ocoByPosition[p.id].fixed_stop_price && (
                                                        <span className="text-red-600 dark:text-red-400 mono ml-2">SL {ocoByPosition[p.id].fixed_stop_price}</span>
                                                    )}
                                                    {ocoByPosition[p.id].trailing_stop_pct && (
                                                        <span className="text-amber-600 dark:text-amber-400 mono ml-2">Trail {ocoByPosition[p.id].trailing_stop_pct}%</span>
                                                    )}
                                                    {ocoByPosition[p.id].pending_open && (
                                                        <span className="ml-2 text-[10px] uppercase tracking-wide">Queued · next open</span>
                                                    )}
                                                </span>
                                                {onCancelOco && (
                                                    <button
                                                        onClick={() => onCancelOco(ocoByPosition[p.id])}
                                                        className="text-muted-foreground hover:text-red-500 transition"
                                                        data-testid={`cancel-oco-${p.id}`}
                                                        aria-label="Cancel OCO strategy"
                                                    >
                                                        <XIcon className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
